import { useState } from 'react';
import type { Rule } from '@/types/entities';
import { ConditionBadge } from '@/components/primitives/ConditionBadge';
import { StatusDot } from '@/components/primitives/StatusDot';
import styles from './ClientViewSection.module.css';

export interface ClientGroupData {
  client: string;
  projects: Array<{
    name: string;
    rules: Rule[];
    enabledCount: number;
  }>;
  totalRules: number;
}

export function ClientViewSection({ groups }: { groups: ClientGroupData[] }) {
  const [collapsedClients, setCollapsedClients] = useState<Set<string>>(new Set());
  const [collapsedProjects, setCollapsedProjects] = useState<Set<string>>(new Set());

  const toggleClient = (client: string) => {
    setCollapsedClients((prev) => {
      const next = new Set(prev);
      if (next.has(client)) next.delete(client);
      else next.add(client);
      return next;
    });
  };

  const toggleProject = (key: string) => {
    setCollapsedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className={styles.clientView}>
      {groups.map((g) => {
        const clientCollapsed = collapsedClients.has(g.client);
        return (
          <div className={styles.clientGroup} key={g.client}>
            <div className={styles.clientGroupHeader} onClick={() => toggleClient(g.client)}>
              <div className={styles.clientGroupLeft}>
                <svg
                  className={`${styles.chevron} ${clientCollapsed ? styles.chevronCollapsed : ''}`}
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="4,3 10,7 4,11" />
                </svg>
                <span className={styles.clientGroupName}>{g.client}</span>
              </div>
              <span className={styles.clientGroupMeta}>
                {g.projects.length} project{g.projects.length !== 1 ? 's' : ''} &middot;{' '}
                {g.totalRules} rule{g.totalRules !== 1 ? 's' : ''}
              </span>
            </div>
            {!clientCollapsed && (
              <div className={styles.clientGroupBody}>
                {g.projects.map((p) => {
                  const projKey = `${g.client}:${p.name}`;
                  const projCollapsed = collapsedProjects.has(projKey);
                  return (
                    <div key={projKey}>
                      <div
                        className={styles.projectGroupHeader}
                        onClick={() => toggleProject(projKey)}
                      >
                        <div className={styles.projectGroupLeft}>
                          <svg
                            className={`${styles.chevron} ${projCollapsed ? styles.chevronCollapsed : ''}`}
                            width="12"
                            height="12"
                            viewBox="0 0 14 14"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <polyline points="4,3 10,7 4,11" />
                          </svg>
                          <span className={styles.projectGroupName}>{p.name}</span>
                        </div>
                        <span className={styles.projectGroupMeta}>
                          {p.enabledCount} of {p.rules.length} rules enabled
                        </span>
                      </div>
                      {!projCollapsed && (
                        <div>
                          {p.rules.map((r) => (
                            <div
                              className={`${styles.clientRuleRow} ${r.status === 'disabled' ? styles.disabledRow : ''}`}
                              key={r.id}
                            >
                              <div className={styles.clientRulePriority}>
                                #{r.priority}
                                <StatusDot tone={r.status === 'enabled' ? 'enabled' : 'disabled'} />
                              </div>
                              <div className={styles.clientRuleConditions}>
                                {r.conditions.map((c, i) => (
                                  <ConditionBadge
                                    key={`${r.id}-cv-${i}`}
                                    type={c.type}
                                    operator={c.operator}
                                    value={c.value}
                                    size="sm"
                                  />
                                ))}
                              </div>
                              <div className={styles.clientRuleEvents}>
                                <span className={styles.eventsZero}>&mdash;</span>
                              </div>
                            </div>
                          ))}
                          <div className={styles.addRuleRow}>+ Add a rule to {p.name}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
